// DraggableAIButton.js - Updated with container constraints
import React, { useState, useRef, useEffect } from 'react';
import './DraggableAIButton.css';

const DraggableAIButton = ({ onOpenAIModal, pinnedCount = 0 }) => {
  const [position, setPosition] = useState(() => {
    const savedPos = localStorage.getItem('aiButtonPosition');
    return savedPos ? JSON.parse(savedPos) : { x: 160, y: 100 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const buttonRef = useRef(null);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const startMouseRef = useRef({ x: 0, y: 0 });
  const dragThresholdRef = useRef(false);

  // Save position to localStorage when it changes
  useEffect(() => {
    if (!isDragging) {
      localStorage.setItem('aiButtonPosition', JSON.stringify(position));
    }
  }, [position, isDragging]);

const constrainPosition = (newX, newY) => {
  // Try to find the actual container the button is in
  const container = buttonRef.current?.closest('.stickies-display-area') || 
                   buttonRef.current?.closest('.content-container');
  
  if (!container) return { x: newX, y: newY };

  const containerRect = container.getBoundingClientRect();
  const buttonSize = 40;
  
  const constrainedX = Math.max(0, Math.min(newX, containerRect.width - buttonSize));
  const constrainedY = Math.max(0, Math.min(newY, containerRect.height - buttonSize));
  
  return { x: constrainedX, y: constrainedY };
};

  const handleMouseDown = (e) => {
    // Prevent default browser behaviors
    e.preventDefault();
    e.stopPropagation();
    
    // Store initial positions
    startPositionRef.current = { ...position };
    startMouseRef.current = { x: e.clientX, y: e.clientY };
    
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    // Calculate delta from start position
    const deltaX = e.clientX - startMouseRef.current.x;
    const deltaY = e.clientY - startMouseRef.current.y;
    
    // Check if we've moved past a minimum threshold
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      dragThresholdRef.current = true;
    }
    
    // Calculate new position
    const newX = startPositionRef.current.x + deltaX;
    const newY = startPositionRef.current.y + deltaY;
    
    // Apply constraints to keep within container
    const constrainedPos = constrainPosition(newX, newY);
    setPosition(constrainedPos);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    // Reset the threshold after a short delay
    setTimeout(() => {
      dragThresholdRef.current = false;
    }, 100);
  };

  // Add and remove event listeners
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Constrain position on mount and container resize
  useEffect(() => {
    const constrainedPos = constrainPosition(position.x, position.y);
    if (constrainedPos.x !== position.x || constrainedPos.y !== position.y) {
      setPosition(constrainedPos);
    }
  }, []);

  const handleClick = (e) => {
    if (isDragging || dragThresholdRef.current) return;
    
    e.stopPropagation();
    onOpenAIModal();
  };

  return (
    <div
      ref={buttonRef}
      className={`draggable-ai-button ${isDragging ? 'dragging' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      title="AI Composition"
    >
      {/* AI Icon - robot emoji */}
      🤖
      {pinnedCount > 0 && (
        <div className="ai-count-badge">{pinnedCount}</div>
      )}
    </div>
  );
};

export default DraggableAIButton;